'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { track } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';
import { ArrowSquareOutIcon, CheckCircleIcon, CircleNotchIcon, WarningCircleIcon } from '@phosphor-icons/react';

import Image from '@/components/ui/Image';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription, useBillingInvoices, usePlans } from '@/hooks/queries/useSubscription';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { useRevalidateMcpSettings } from '@/hooks/queries/useMcpSettings';
import { useProfile } from '@/hooks/queries/useProfile';
import { subscriptionsApi, ApiError, type BillingInvoiceDto } from '@/lib/api';
import type { PlanDto, SubscriptionDto } from '@/lib/api';
import { getSubscriptionTierImageSrc } from '@/lib/subscription-tier-images';
import { cn } from '@/lib/utils';

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    new Date(value),
  );
}

function formatInvoicePeriod(start: string | null | undefined, end: string | null | undefined): string {
  const startLabel = start ? formatDate(start) : null;
  const endLabel = end ? formatDate(end) : null;

  if (startLabel && endLabel) {
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  }

  return startLabel ?? endLabel ?? '-';
}

function formatLimit(value: number | null, singular: string, plural: string): string {
  if (value === null) return 'Conexões bancárias ilimitadas';
  return `${value} ${value === 1 ? singular : plural}`;
}

function planFeatures(plan: PlanDto): string[] {
  const features = [
    formatLimit(plan.maxBankConnections, 'conexão bancária', 'conexões bancárias'),
    'Acesso completo ao fluxo de caixa',
    'Metas, orçamentos e investimentos',
  ];

  if (plan.hasExport || plan.hasAdvancedAnalytics) {
    features.push('Assistente inteligente');
  }

  return features;
}

function statusBadge(status: string, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd) return <Badge variant="outline">Cancelando ao fim do ciclo</Badge>;
  switch (status.toLowerCase()) {
    case 'active':
      return <Badge variant="default">Ativo</Badge>;
    case 'trialing':
      return <Badge variant="info">Em avaliação</Badge>;
    case 'trialexpired':
      return <Badge variant="outline">Avaliação expirada</Badge>;
    case 'pastdue':
    case 'past_due':
      return <Badge variant="error">Pagamento pendente</Badge>;
    case 'canceled':
      return <Badge variant="outline">Cancelado</Badge>;
    case 'unpaid':
      return <Badge variant="error">Não pago</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// Concept card language (v2 tokens — resolves inside the ProfileView data-tokens="v2" subtree).
const V2_CARD = 'rounded-[var(--radius-3xl)] border-border bg-surface shadow-[var(--shadow-1)]';

function planSummaryDescription(sub: SubscriptionDto): string | null {
  if (sub.cancelAtPeriodEnd && sub.currentPeriodEnd) return `Termina em ${formatDate(sub.currentPeriodEnd)}`;
  if (!sub.hasManagedSubscription && sub.trialEndsAt) {
    const expired = sub.status.toLowerCase() === 'trialexpired';
    return expired
      ? `Avaliação encerrada em ${formatDate(sub.trialEndsAt)}`
      : `Avaliação termina em ${formatDate(sub.trialEndsAt)}`;
  }
  if (sub.hasManagedSubscription && sub.currentPeriodEnd) {
    return `${formatCurrency(sub.plan.unitAmountCents, sub.plan.currency)} / mês · renova em ${formatDate(sub.currentPeriodEnd)}`;
  }
  return null;
}

function isBillingAddressRequired(err: unknown): boolean {
  return err instanceof ApiError
    && err.status === 409
    && (err.payload as { code?: string } | undefined)?.code === 'billing_address_required';
}

// Opening the Stripe billing portal — shared by PlanSummary and CurrentPlanCard so the
// managed-subscriber "Gerenciar assinatura" action stays reachable from whichever surface renders.
function useBillingPortal() {
  const authedFetch = useAuthedFetcher();
  const [openingPortal, setOpeningPortal] = useState(false);

  async function openPortal() {
    setOpeningPortal(true);
    track('action.billing.open_portal');
    try {
      const { url } = await authedFetch((token) => subscriptionsApi.openPortal(token));
      window.location.href = url;
    } catch (err) {
      notifyError('billing', err, 'Não foi possível abrir o portal.');
      setOpeningPortal(false);
    }
  }

  return { openingPortal, openPortal };
}

// The current plan rendered as a prominent inline summary (eyebrow + plan name + status
// badge + a one-line description) — a primary account fact, weighted above the plain fields
// without resorting to a card. Managed subscribers keep their portal action here, since the
// profile suppresses CurrentPlanCard (showCurrentPlan={false}).
export function PlanSummary() {
  const sub = useSubscription();
  const s = sub.data;
  const description = s ? planSummaryDescription(s) : null;
  const { openingPortal, openPortal } = useBillingPortal();

  return (
    <div>
      <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">Plano</p>
      {sub.isLoading || !s ? (
        <div className="mt-2 h-5 w-36 animate-pulse rounded bg-surface-sunken" />
      ) : (
        <>
          <p className="mt-1.5 text-lg font-semibold text-text-primary">{s.planName}</p>
          {description && <p className="mt-1 text-sm text-text-secondary">{description}</p>}
          {s.hasManagedSubscription && (
            <Button variant="outline" size="sm" className="mt-3" onClick={openPortal} disabled={openingPortal}>
              {openingPortal ? 'Abrindo...' : 'Gerenciar assinatura'}
              <ArrowSquareOutIcon className="size-3.5" weight="bold" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

type BillingViewProps = {
  embedded?: boolean;
  showCurrentPlan?: boolean;
  /** Assinar pre-flight (VER-389): called instead of the checkout API when the
   * billing address is incomplete; the host (ProfileView) opens the form and
   * hands back resumePlanCode once the address saves. */
  onBillingAddressRequired?: (plan: { code: string; name: string }) => void;
  /** Set by the host after the address saves — resumes the interrupted checkout. */
  resumePlanCode?: string | null;
  onResumeConsumed?: () => void;
};

export function BillingView({
  embedded = false,
  showCurrentPlan = true,
  onBillingAddressRequired,
  resumePlanCode = null,
  onResumeConsumed,
}: BillingViewProps) {
  const sub = useSubscription();
  const showPlanOptions = !!sub.data && !sub.data.hasManagedSubscription;

  const plansViewTracked = useRef(false);
  useEffect(() => {
    if (!showPlanOptions || plansViewTracked.current) return;
    plansViewTracked.current = true;
    track('billing.plans_viewed');
  }, [showPlanOptions]);
  const plans = usePlans(showPlanOptions);
  const invoices = useBillingInvoices();
  const [searchParams, setSearchParams] = useSearchParams();
  const authedFetch = useAuthedFetcher();
  const revalidateMcpSettings = useRevalidateMcpSettings();
  const { openingPortal, openPortal } = useBillingPortal();
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);

  // Post-checkout polling: webhook may not have arrived yet. Once the subscription is confirmed,
  // the connector section (it carries the plan too) refreshes with it.
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success') return;
    const sessionId = searchParams.get('session_id');
    track('action.billing.checkout_complete', {});
    toast.success('Pagamento confirmado. Atualizando seu plano...');
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (sessionId) {
        try {
          const synced = await authedFetch((token) => subscriptionsApi.syncCheckoutSession(token, sessionId));
          if (cancelled) return;
          if (synced?.hasManagedSubscription) {
            await sub.mutate();
            await revalidateMcpSettings();
            return;
          }
        } catch {
          // Webhook polling below is the fallback when Stripe is still finalizing the session.
        }
      }

      while (!cancelled && attempts < 3) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 1500));
        const updated = await sub.mutate();
        if (updated?.hasManagedSubscription) {
          await revalidateMcpSettings();
          return;
        }
      }
    };
    void poll();
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Assinar" promises "you are leaving now" — discovering a missing form after a
  // spinner is the worst place, so the incomplete-address case never calls the API:
  // the host scrolls to the form and resumes the checkout after the save. The
  // server 409 stays as the backstop below for the window where the flag is stale.
  const { data: profile } = useProfile();

  function planNameFor(planCode: string): string {
    return plans.data?.find((plan) => plan.code === planCode)?.name ?? planCode;
  }

  async function handleCheckout(planCode: string) {
    // Only an explicit false pre-flights: on a pre-field API the flag is absent,
    // and blocking there would open a form whose endpoint does not exist —
    // undefined proceeds to checkout and the 409 backstop covers the rest.
    if (onBillingAddressRequired && profile?.billingAddressComplete === false) {
      onBillingAddressRequired({ code: planCode, name: planNameFor(planCode) });
      return;
    }
    setPendingPlan(planCode);
    // Before the redirect fetch so the event isn't lost to the page unload.
    track('action.billing.checkout_start', { plan_code: planCode });
    try {
      const { url } = await authedFetch((token) => subscriptionsApi.startCheckout(token, planCode));
      window.location.href = url;
    } catch (err) {
      // Local type-guard, deliberately not a sibling of lib/upgradeRequired.ts —
      // 402s fire from anywhere, this only ever happens on this one button.
      if (isBillingAddressRequired(err) && onBillingAddressRequired) {
        onBillingAddressRequired({ code: planCode, name: planNameFor(planCode) });
        setPendingPlan(null);
        return;
      }
      notifyError('billing', err, 'Não foi possível iniciar o checkout.');
      setPendingPlan(null);
    }
  }

  // The host signals the address just saved during an interrupted Assinar.
  const resumeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resumePlanCode) {
      // The guard exists only for the window before onResumeConsumed clears the
      // prop; once it clears, forget the code — a SECOND interrupted Assinar for
      // the same plan (e.g. the resumed checkout 409'd again) must resume too.
      resumeRef.current = null;
      return;
    }
    if (resumeRef.current === resumePlanCode) return;
    if (profile?.billingAddressComplete !== true) return;
    resumeRef.current = resumePlanCode;
    onResumeConsumed?.();
    void handleCheckout(resumePlanCode);
    // handleCheckout is re-created per render; the ref guards re-entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePlanCode, profile?.billingAddressComplete]);

  return (
    <div className={cn('flex flex-col gap-6', !embedded && 'mx-auto max-w-5xl p-4 sm:p-6')}>
      {!embedded && (
        <header>
          <h1 className="text-2xl font-bold text-text-primary">Assinatura</h1>
          <p className="text-sm text-text-secondary">Gerencie seu plano, pagamento e faturas.</p>
        </header>
      )}

      {showCurrentPlan && (
        <CurrentPlanCard
          subscription={sub.data}
          loading={sub.isLoading}
          onOpenPortal={openPortal}
          openingPortal={openingPortal}
        />
      )}

      {showPlanOptions && (
        <PlanOptionsCard
          plans={plans.data}
          loading={plans.isLoading}
          error={plans.error}
          currentPlanCode={sub.data?.planCode}
          pendingPlan={pendingPlan}
          onCheckout={handleCheckout}
        />
      )}

      <InvoicesCard
        invoices={invoices.data}
        loading={invoices.isLoading}
        error={invoices.error}
        onOpenInvoice={async (invoiceId) => {
          try {
            const { url } = await authedFetch((token) => subscriptionsApi.invoiceHostedUrl(token, invoiceId));
            track('action.billing.open_invoice');
            window.open(url, '_blank', 'noopener,noreferrer');
          } catch (err) {
            notifyError('billing', err, 'Não foi possível abrir a fatura.');
          }
        }}
      />
    </div>
  );
}

function CurrentPlanCard({
  subscription,
  loading,
  onOpenPortal,
  openingPortal,
}: {
  subscription: SubscriptionDto | undefined;
  loading: boolean;
  onOpenPortal: () => void;
  openingPortal: boolean;
}) {
  if (loading || !subscription) {
    return <CircleNotchIcon className="size-5 animate-spin text-text-secondary" />;
  }

  const hasManagedSubscription = subscription.hasManagedSubscription;
  const trialExpired = subscription.status.toLowerCase() === 'trialexpired';
  const isLocalTrial = !hasManagedSubscription && subscription.planCode === 'trial';
  const trialPlanLimit = subscription.plan.maxBankConnections ?? subscription.entitlements.maxBankConnections;
  const planImageSrc =
    getSubscriptionTierImageSrc(subscription.planCode) ?? getSubscriptionTierImageSrc(subscription.plan.code);
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="flex min-w-0 items-start gap-3">
          {planImageSrc && (
            <div className="size-16 shrink-0 overflow-hidden rounded-[var(--radius-xl)] bg-surface-sunken sm:size-18">
              <Image
                src={planImageSrc}
                alt=""
                width={72}
                height={72}
                className="size-full object-contain"
              />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-text-primary">{subscription.planName}</p>
            <p className="text-sm text-text-secondary">
              {isLocalTrial
                ? `${formatLimit(trialPlanLimit, 'conexão bancária', 'conexões bancárias')} durante a avaliação`
                : `${formatCurrency(subscription.plan.unitAmountCents, subscription.plan.currency)} / mês`}
            </p>
            <div className="mt-2">{statusBadge(subscription.status, subscription.cancelAtPeriodEnd)}</div>
          </div>
        </div>
        {hasManagedSubscription && (
          <Button className="w-full sm:w-auto" onClick={onOpenPortal} disabled={openingPortal}>
            {openingPortal ? 'Abrindo...' : 'Gerenciar assinatura'}
          </Button>
        )}
      </div>
      {(hasManagedSubscription || subscription.trialEndsAt) && (
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
          {hasManagedSubscription && (
            <div>
              <div className="text-text-secondary">Próxima cobrança</div>
              <div className="font-medium">{formatDate(subscription.currentPeriodEnd)}</div>
            </div>
          )}
          {subscription.trialEndsAt && (
            <div>
              <div className="text-text-secondary">
                {trialExpired ? 'Avaliação terminou em' : 'Avaliação termina em'}
              </div>
              <div className="font-medium">{formatDate(subscription.trialEndsAt)}</div>
            </div>
          )}
          {subscription.cancelAtPeriodEnd && (
            <div className="flex items-center gap-2 text-warning-text sm:col-span-3">
              <WarningCircleIcon />
              <span>Sua assinatura termina em {formatDate(subscription.currentPeriodEnd)}. Reative no portal.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanOptionsCard({
  plans,
  loading,
  error,
  currentPlanCode,
  pendingPlan,
  onCheckout,
}: {
  plans: PlanDto[] | undefined;
  loading: boolean;
  error: unknown;
  currentPlanCode: string | undefined;
  pendingPlan: string | null;
  onCheckout: (planCode: string) => void;
}) {
  if (loading) {
    return (
      <Card variant="flat" className={V2_CARD}>
        <CardContent className="p-6">
          <CircleNotchIcon className="size-5 animate-spin text-text-secondary" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const message = error instanceof ApiError ? error.message : 'Não foi possível carregar os planos.';
    return (
      <Card variant="flat" className={V2_CARD}>
        <CardHeader>
          <CardTitle>Escolha seu plano</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!plans || plans.length === 0) return null;

  return (
    <section className="flex flex-col gap-4">
      <div>
        <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Escolha seu plano
        </p>
        <p className="mt-1 text-sm text-text-secondary">Assine Starter ou Pro para liberar seu acesso.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        {plans.map((plan) => {
          const isTrialPlan = plan.code === currentPlanCode;
          const recommended = plan.code === 'pro';
          const disabled = pendingPlan !== null || !plan.canCheckout;
          const planImageSrc = getSubscriptionTierImageSrc(plan.code);

          return (
            <div
              key={plan.id}
              // data-brand-fill (recommended only): keep the deep-emerald substrate
              // in dark mode so the white text, the gold "Recomendado" badge and the
              // white "Assinar" button stay legible (the role remap would flip the
              // fill to light sage and wash them all out). See globals.css.
              data-brand-fill={recommended || undefined}
              className={cn(
                'flex flex-col justify-between gap-5 rounded-[var(--radius-3xl)] border p-5',
                recommended
                  ? 'border-transparent bg-brand-primary text-on-brand shadow-[0_12px_30px_-16px_rgba(14,90,74,0.45)]'
                  : 'border-border bg-surface shadow-[var(--shadow-1)]',
              )}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    {planImageSrc && (
                      <div className={cn(
                        'size-16 shrink-0 overflow-hidden rounded-lg sm:size-18',
                        recommended ? 'bg-on-brand/10' : 'bg-surface-sunken',
                      )}>
                        <Image
                          src={planImageSrc}
                          alt=""
                          width={72}
                          height={72}
                          className="size-full object-contain"
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className={cn('truncate text-lg font-black', recommended ? 'text-on-brand' : 'text-text-primary')}>
                        {plan.name}
                      </h3>
                      <p className={cn('text-sm font-medium', recommended ? 'text-on-brand/70' : 'text-text-secondary')}>
                        {formatCurrency(plan.unitAmountCents, plan.currency)} / mês
                      </p>
                    </div>
                  </div>
                  {isTrialPlan ? (
                    <Badge variant="outline">Plano da avaliação</Badge>
                  ) : recommended ? (
                    <span className="shrink-0 rounded-full bg-[#D9C58A] px-2.5 py-1 font-mono text-3xs font-semibold tracking-[0.08em] text-brand-primary uppercase">
                      Recomendado
                    </span>
                  ) : null}
                </div>

                <ul className="space-y-2 text-sm">
                  {planFeatures(plan).map((feature) => (
                    <li key={feature} className={cn('flex gap-2', recommended ? 'text-on-brand/90' : 'text-text-secondary')}>
                      <CheckCircleIcon
                        className={cn('size-4 shrink-0', recommended ? 'text-on-brand' : 'text-brand-primary')}
                        weight="regular"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                className={cn('w-full', recommended && 'bg-surface text-brand-primary hover:bg-surface/90')}
                variant={recommended ? 'secondary' : 'outline'}
                disabled={disabled}
                onClick={() => onCheckout(plan.code)}
              >
                {pendingPlan === plan.code ? 'Redirecionando...' : `Assinar ${plan.name}`}
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function InvoicesCard({
  invoices,
  loading,
  error,
  onOpenInvoice,
}: {
  invoices: BillingInvoiceDto[] | undefined;
  loading: boolean;
  error: unknown;
  onOpenInvoice: (invoiceId: string) => void;
}) {
  // Until Phase 3 lands the endpoint may 404; hide the card silently in that case.
  if (error instanceof ApiError && (error.status === 404 || error.status === 501)) return null;
  if (loading) return null;
  if (!invoices || invoices.length === 0) {
    return (
      <div>
        <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">Faturas</p>
        <p className="mt-1 text-sm text-text-secondary">Nenhuma fatura ainda.</p>
      </div>
    );
  }

  return (
    <Card variant="flat" className={V2_CARD}>
      <CardHeader>
        <CardTitle>Faturas</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-divider sm:hidden">
          {invoices.map((inv) => (
              <div key={inv.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-mono text-base font-semibold text-text-primary tabular-nums">
                      {formatCurrency(inv.amountDueCents, inv.currency)}
                    </p>
                    <p className="mt-1 text-xs font-medium text-text-secondary">
                      Emitida em {formatDate(inv.createdAt)}
                    </p>
                  </div>
                  <InvoiceStatusBadge status={inv.status} />
                </div>

                <dl className="mt-3 grid gap-1 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-secondary">Período</dt>
                    <dd className="text-right font-medium text-text-primary">
                      {formatInvoicePeriod(inv.periodStart, inv.periodEnd)}
                    </dd>
                  </div>
                </dl>

                {inv.hasHostedInvoice && (
                  <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => onOpenInvoice(inv.id)}>
                    Ver
                    <ArrowSquareOutIcon className="size-3.5" weight="bold" />
                  </Button>
                )}
              </div>
          ))}
        </div>

        <table className="hidden w-full text-sm sm:table">
          <thead className="text-left">
            <tr className="font-mono text-2xs font-semibold tracking-[0.08em] text-text-tertiary uppercase">
              <th className="px-4 py-2.5 font-semibold sm:px-6">Emissão</th>
              <th className="px-4 py-2.5 font-semibold sm:px-6">Período</th>
              <th className="px-4 py-2.5 font-semibold sm:px-6">Valor</th>
              <th className="px-4 py-2.5 font-semibold sm:px-6">Status</th>
              <th className="px-4 py-2.5 font-semibold sm:px-6">Ações</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-divider">
                <td className="px-4 py-2 sm:px-6">{formatDate(inv.createdAt)}</td>
                <td className="px-4 py-2 sm:px-6">
                  {formatInvoicePeriod(inv.periodStart, inv.periodEnd)}
                </td>
                <td className="px-4 py-2 font-mono font-medium tabular-nums sm:px-6">{formatCurrency(inv.amountDueCents, inv.currency)}</td>
                <td className="px-4 py-2 sm:px-6">
                  <InvoiceStatusBadge status={inv.status} />
                </td>
                <td className="px-4 py-2 sm:px-6">
                  {inv.hasHostedInvoice && (
                    <Button variant="outline" size="sm" onClick={() => onOpenInvoice(inv.id)}>
                      Ver
                      <ArrowSquareOutIcon className="size-3.5" weight="bold" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  switch (status.toLowerCase()) {
    case 'paid':
      return (
        <span className="inline-flex items-center gap-1 text-success-text">
          <CheckCircleIcon /> Paga
        </span>
      );
    case 'open':
      return <Badge variant="outline">Aberta</Badge>;
    case 'void':
      return <Badge variant="outline">Anulada</Badge>;
    case 'uncollectible':
      return <Badge variant="error">Incobrável</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
